import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, List, GanttChartSquare } from "lucide-react";
import { useParams } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { TaskDialog } from "@/components/action/TaskDialog";
import { TaskRow } from "@/components/action/TaskRow";
import { TaskTimeline } from "@/components/action/TaskTimeline";
import { LiveBoard } from "@/components/action/LiveBoard";
import { AssignablePanels } from "@/components/action/AssignablePanels";
import { groupByDepartmentAndUser } from "@/components/action/grouping";
import { TASK_DEPARTMENTS, TASK_PRIORITIES, TASK_STATUSES, ROLE_LABELS } from "@/constants/workflow";
import { api, apiError } from "@/lib/api";

const SUMMARY = [
  ["To Do", "todo", "text-slate-700"],
  ["In Progress", "in_progress", "text-amber-600"],
  ["Due Today", "due_today", "text-cyan-700"],
  ["Overdue", "overdue", "text-red-600"],
];

export default function ActionPage() {
  const { projectId } = useParams();
  const { capabilities } = useAuth();
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("mine");
  const [department, setDepartment] = useState("all");
  const [assignee, setAssignee] = useState("all");
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [view, setView] = useState("list");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [panels, setPanels] = useState([]);
  const [phases, setPhases] = useState([]);
  const [pools, setPools] = useState([]);
  const [preset, setPreset] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/tasks`);
      setData(res.data);
      if (res.data.can_manage) {
        const pool = await api.get(`/projects/${projectId}/tasks/assignable`);
        setPools(pool.data.pools || []);
      }
    } catch (error) {
      toast.error(apiError(error));
    }
  }, [projectId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!data?.can_manage) return;
    api
      .get(`/projects/${projectId}/panels`)
      .then((res) =>
        setPanels(
          (res.data.panels || []).map((p) => ({
            panel_uid: p.panel_uid,
            panel_name: p.panel_name,
          })),
        ),
      )
      .catch(() => setPanels([]));
    api
      .get(`/projects/${projectId}/planning`)
      .then((res) => setPhases(res.data.phases || []))
      .catch(() => setPhases([]));
  }, [projectId, data?.can_manage]);

  const people = useMemo(() => {
    const map = new Map();
    (data?.tasks || []).forEach((task) =>
      task.assignees.forEach((a) => map.set(a.id, a.name)),
    );
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data]);

  const visible = useMemo(() => {
    let rows = data?.tasks || [];
    if (data?.scope_mode === "mine_all" && scope === "mine")
      rows = rows.filter((t) => t.assignee_ids.includes(data.me));
    if (data?.scope_mode === "departments" && department !== "all")
      rows = rows.filter((t) => (t.departments || []).includes(department));
    if (assignee !== "all") rows = rows.filter((t) => t.assignee_ids.includes(assignee));
    if (status !== "all") rows = rows.filter((t) => t.status === status);
    if (priority !== "all") rows = rows.filter((t) => t.priority === priority);
    return rows;
  }, [data, scope, department, assignee, status, priority]);

  const hierarchical = data?.scope_mode === "departments" && department === "all";
  const groups = useMemo(
    () => (hierarchical ? groupByDepartmentAndUser(visible) : []),
    [hierarchical, visible],
  );

  const setTaskStatus = async (task, value) => {
    try {
      await api.patch(`/projects/${projectId}/tasks/${task.id}`, { status: value });
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const removeTask = async (task) => {
    try {
      await api.delete(`/projects/${projectId}/tasks/${task.id}`);
      toast.success("Task deleted");
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  if (!data)
    return (
      <main className="flex flex-1 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
      </main>
    );

  return (
    <main
      data-testid="action-page"
      className="okn-scroll mx-auto w-full max-w-[1500px] flex-1 overflow-y-auto px-4 py-5 sm:px-6"
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="mono flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] uppercase tracking-wider">
          {SUMMARY.map(([label, key, color], index) => (
            <span key={key} className="flex items-center gap-3">
              {index ? <span className="text-slate-300">·</span> : null}
              <span data-testid={`task-summary-${key}`} className="text-slate-500">
                {label} <span className={`text-[13px] ${color}`}>{data.summary[key]}</span>
              </span>
            </span>
          ))}
        </div>
        {data.can_manage && !capabilities?.is_guest ? (
          <button
            data-testid="add-task-button"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            className="mono ml-auto flex items-center gap-1.5 rounded-full bg-cyan-600 px-4 py-2 text-[10.5px] uppercase tracking-wider text-white transition-colors hover:bg-cyan-700"
          >
            <Plus size={13} /> Add task
          </button>
        ) : null}
      </div>

      <div className="mt-3 flex rounded-full border border-slate-200 bg-white p-0.5 sm:w-fit">
        {[
          ["list", "List View", List],
          ["timeline", "Timeline View", GanttChartSquare],
        ].map(([key, label, Icon]) => (
          <button
            key={key}
            data-testid={`task-view-${key}`}
            onClick={() => setView(key)}
            className={`mono flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-[10.5px] uppercase tracking-wider transition-colors ${
              view === key
                ? "bg-cyan-500/10 text-cyan-700"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {data.can_manage ? (
        <AssignablePanels
          pools={pools}
          onCreateTask={(pool, uids) => {
            setEditing(null);
            setPreset({
              name: `${ROLE_LABELS[pool.department]} work — ${uids.length} panel(s)`,
              panel_uids: uids,
            });
            setDialogOpen(true);
          }}
        />
      ) : null}

      <div
        className={`mt-4 grid gap-4 ${
          data.can_view_live ? "lg:grid-cols-[minmax(0,1fr)_320px]" : "grid-cols-[minmax(0,1fr)]"
        }`}
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {data.scope_mode === "mine_all" ? (
              <div className="flex rounded-full border border-slate-200 bg-white p-0.5">
                {[
                  ["mine", "My Tasks"],
                  ["all", "All Tasks"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    data-testid={`task-scope-${key}`}
                    onClick={() => setScope(key)}
                    className={`mono rounded-full px-3.5 py-1.5 text-[10.5px] uppercase tracking-wider transition-colors ${
                      scope === key
                        ? "bg-cyan-500/10 text-cyan-700"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}

            {data.scope_mode === "departments" ? (
              <div className="flex flex-wrap rounded-full border border-slate-200 bg-white p-0.5">
                {TASK_DEPARTMENTS.map((dept) => (
                  <button
                    key={dept.key}
                    data-testid={`task-department-${dept.key}`}
                    onClick={() => setDepartment(dept.key)}
                    className={`mono rounded-full px-3 py-1.5 text-[10.5px] uppercase tracking-wider transition-colors ${
                      department === dept.key
                        ? "bg-cyan-500/10 text-cyan-700"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {dept.label}
                  </button>
                ))}
              </div>
            ) : null}

            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger
                data-testid="task-filter-assignee"
                className="mono h-9 w-[150px] border-slate-200 bg-white text-[10.5px] uppercase tracking-wider"
              >
                <SelectValue placeholder="Assignee" />
              </SelectTrigger>
              <SelectContent className="border-slate-200 bg-white text-slate-800">
                <SelectItem value="all" className="text-xs">
                  All assignees
                </SelectItem>
                {people.map(([id, name]) => (
                  <SelectItem key={id} value={id} className="text-xs">
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger
                data-testid="task-filter-status"
                className="mono h-9 w-[140px] border-slate-200 bg-white text-[10.5px] uppercase tracking-wider"
              >
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="border-slate-200 bg-white text-slate-800">
                <SelectItem value="all" className="text-xs">
                  All statuses
                </SelectItem>
                {TASK_STATUSES.map((value) => (
                  <SelectItem key={value} value={value} className="text-xs">
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger
                data-testid="task-filter-priority"
                className="mono h-9 w-[140px] border-slate-200 bg-white text-[10.5px] uppercase tracking-wider"
              >
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent className="border-slate-200 bg-white text-slate-800">
                <SelectItem value="all" className="text-xs">
                  All priorities
                </SelectItem>
                {TASK_PRIORITIES.map((value) => (
                  <SelectItem key={value} value={value} className="text-xs">
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-3 min-w-0 space-y-2" data-testid="task-list">
            {view === "timeline" ? (
              <TaskTimeline
                tasks={visible}
                today={data.today}
                canManage={data.can_manage}
                hierarchical={hierarchical}
                onEdit={(row) => {
                  setEditing(row);
                  setDialogOpen(true);
                }}
              />
            ) : visible.length === 0 ? (
              <p
                data-testid="task-list-empty"
                className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500"
              >
                No tasks match these filters.
              </p>
            ) : hierarchical ? (
              groups.map((group) => (
                <div key={group.key} data-testid={`task-group-${group.key}`} className="space-y-2">
                  <p className="mono mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-600">
                    {group.label}
                    <span className="rounded bg-slate-500/10 px-1.5 py-0.5 text-[9.5px] text-slate-500">
                      {group.count}
                    </span>
                  </p>
                  {group.people.map((person, personIndex) => (
                    <div
                      key={person.id}
                      data-testid={`task-group-${group.key}-${person.id}`}
                      className={`space-y-2 border-l-2 border-slate-200 pl-3 ${
                        personIndex ? "border-t border-t-slate-300 pt-3" : ""
                      }`}
                    >
                      <p className="mono text-[10.5px] text-slate-500">
                        {person.name} · {person.tasks.length}
                      </p>
                      {person.tasks.map((task) => (
                        <TaskRow
                          key={`${person.id}-${task.id}`}
                          task={task}
                          today={data.today}
                          canManage={data.can_manage}
                          isAssignee={task.assignee_ids.includes(data.me)}
                          onStatus={setTaskStatus}
                          onEdit={(row) => {
                            setEditing(row);
                            setDialogOpen(true);
                          }}
                          onDelete={removeTask}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              visible.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  today={data.today}
                  canManage={data.can_manage}
                  isAssignee={task.assignee_ids.includes(data.me)}
                  onStatus={setTaskStatus}
                  onEdit={(row) => {
                    setEditing(row);
                    setDialogOpen(true);
                  }}
                  onDelete={removeTask}
                />
              ))
            )}
          </div>
        </div>

        {data.can_view_live ? <LiveBoard live={data.live} /> : null}
      </div>

      <TaskDialog
        projectId={projectId}
        task={editing}
        preset={preset}
        users={data.assignable_users}
        panels={panels}
        phases={phases}
        open={dialogOpen}
        onOpenChange={(next) => {
          setDialogOpen(next);
          if (!next) setPreset(null);
        }}
        onSaved={load}
      />
    </main>
  );
}
